import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { trustChips } from '../../data/homeContent';
import { BOOKING_URL } from '../../data/site';
import { IMAGES } from '../../data/images';
import { ImageSlot } from '../ui/ImageSlot';
import { ButtonLink } from '../ui/ButtonLink';
import { Star } from '../ui/StarDivider';
export type HeroHeadline = 'train-like-your-life' | 'real-world-defense';
const lines: Record<HeroHeadline, {
  headline: string;
  subLine: string;
}> = {
  'train-like-your-life': {
    headline: 'Train Like Your Life Depends On It.',
    subLine: 'Real-World Defense Skills. Las Vegas’ Trusted CCW Academy.'
  },
  'real-world-defense': {
    headline: 'Real-World Defense Skills.',
    subLine: 'Las Vegas’ Trusted CCW Academy — Train Like Your Life Depends On It.'
  }
};
export function Hero({
  headline = 'train-like-your-life'
}: {
  headline?: HeroHeadline;
}) {
  const reduce = useReducedMotion();
  const copy = lines[headline];
  const enter = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease: [0.23, 1, 0.32, 1] as const
    }
  });
  return <section className="under-header relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-ink" aria-labelledby="hero-title" data-id="element-159" data-mp="1cyodhbgeptdc">
      <ImageSlot image={IMAGES.hero} eager className="absolute inset-0 -z-10" imgClassName="ken-burns object-[68%_center]" data-id="element-160" data-mp="1hxrkh1fy69ny" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-ink via-ink/80 to-midnight/10" aria-hidden="true" data-id="element-161" data-mp="25p6g6bd7xsq8" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-2/5 bg-gradient-to-t from-ink via-ink/60 to-transparent" aria-hidden="true" data-id="element-162" data-mp="1vxahay3b5yky" />

      <div className="gutter flex flex-1 flex-col justify-center py-[clamp(3rem,8vh,7rem)]" data-id="element-163" data-mp="20v2b5xnijurs">
        <motion.p {...enter(0)} className="font-display text-[clamp(0.95rem,1.2vw,1.35rem)] font-medium uppercase tracking-[0.14em] text-chrome-300" data-id="element-164" data-mp="2okn3h75k44dq">
          {copy.subLine}
        </motion.p>
        <motion.h1 {...enter(0.05)} id="hero-title" className="t-display mt-5 max-w-[13ch] text-white" data-id="element-165" data-mp="338n38yu1p76h">
          {copy.headline}
        </motion.h1>
        <motion.span {...enter(0.1)} className="red-rule mt-7" aria-hidden="true" data-id="element-166" data-mp="2y7hbp4c4fpyj" />
        <motion.p {...enter(0.12)} className="t-lead mt-7 max-w-[44rem] text-chrome-100" data-id="element-167" data-mp="2u5vsz2cwohgf">
          Nevada &amp; Multi-State CCW permits, renewals and real-world defensive training in Las Vegas. Taught by CCW Maggie and a team of vetted instructors.
        </motion.p>
        <motion.div {...enter(0.18)} className="mt-9 flex flex-wrap gap-3" data-id="element-168" data-mp="2fhrhaic9t675">
          <ButtonLink href={BOOKING_URL} size="lg" data-id="element-169" data-mp="2aj7tsdi13mco">
            Book Your CCW Class
          </ButtonLink>
          <ButtonLink to="/courses" size="lg" variant="outline-light" data-id="element-170" data-mp="26ixda6wm1djk">
            Find the Right Course
          </ButtonLink>
        </motion.div>
      </div>

      <motion.div {...enter(0.24)} className="gutter pb-[clamp(2rem,5vh,3.5rem)]" data-id="element-171" data-mp="21kvw5riqvoaq">
        <div className="chrome-line mb-6 opacity-30" aria-hidden="true" data-id="element-172" data-mp="1wkq8snjicnyg" />
        <ul className="flex flex-wrap gap-x-7 gap-y-3" aria-label="Credentials" data-id="element-173" data-mp="c14ci4iogdsz">
          {trustChips.map(chip => <li key={chip} className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-chrome-100 md:text-[15px]" data-id="element-174" data-mp="gz5xiuznyn73">
              <Star className="h-3.5 w-3.5 text-chrome-300" data-id="element-175" data-mp="q1f5bd3js2gs" />
              {chip}
            </li>)}
        </ul>
      </motion.div>
    </section>;
}